import { I18nProvider, useI18n } from "../i18n/I18nManager";
import { TabsLayout } from "../components/TabsLayout";
import {
  PaneLayout,
  SidePane,
  FullscreenPane,
} from "../components/PaneLayout/PaneLayout";
import { BibleSelector } from "../components/BibleSelector/BibleSelector";
import { BibleReaderToolbar } from "../components/BibleReaderToolbar/BibleReaderToolbar";
import { FloatingReaderPanels } from "../components/FloatingReaderPanels/FloatingReaderPanels";
import { Sidebar, SharedSessionsToasts } from "../components/Tabs/Tabs";
import { createSeedBibleState } from "../managers/SeedBibleStateManager";
import { Suspense } from "preact/compat";
import { useEffect } from "preact/hooks";
import { useSignalEffect, type ReadonlySignal } from "@preact/signals";
import { closeContextMenus } from "../components/ContextMenu/ContextMenu";
import { ModalHost } from "../components/ModalHost/ModalHost";
import { ToastHost } from "../components/ToastHost/ToastHost";
import { LoginModal } from "../components/LoginModal/LoginModal";
import { TermsOfServiceModal } from "../components/TermsOfServiceModal/TermsOfServiceModal";
import { PrivacyPolicyModal } from "../components/PrivacyPolicyModal/PrivacyPolicyModal";
import { CodeOfConductModal } from "../components/CodeOfConductModal/CodeOfConductModal";
import { useMemo } from "preact/hooks";
import {
  AppConfigProvider,
  DEFAULT_APP_CONFIG,
  useAppConfig,
  type AppConfig,
} from "./appConfig";
import { isWebKit } from "./ssrEnv";
// Foundation stylesheets — must load before any component's co-located CSS.
// `variables` (the :root tokens) and `base` (html/body reset) come first so
// every component rule resolves against them.
import "./styles/base.inline.css";
import "./styles/utilities.inline.css";
import {
  OnboardingModals,
  LanguageUnavailableModal,
  UiLanguageSwitchModal,
} from "../components/Onboarding/Onboarding";
import { Tutorial } from "../components/Tutorial/Tutorial";
import { TutorialPrompt } from "../components/TutorialPrompt/TutorialPrompt";
import { OfflineDownloadPrompt } from "../components/OfflineDownloadPrompt/OfflineDownloadPrompt";

/**
 * Font `<link>`s, plus the CSS for the active Customization layered on top
 * of the real theme (see `SeedBibleStateManager`'s `theme`/`themeCssVariables`/
 * `themeCssClasses`). The unblended preset+settings theme writes directly to
 * `document.head` from a `ThemeManager` effect instead (see `ThemeManager.tsx`'s
 * `createTheme`) — that target is never diffed by Preact, so it carries no
 * hydration-mismatch risk the way an in-tree `<style>` would; this one still
 * needs to be in-tree so a shared `?customization=` link's colors are part of
 * the SSR'd HTML. `dangerouslySetInnerHTML` (rather than a plain text child)
 * is what keeps it safe: raw CSS can contain `&` (e.g. the highlight classes'
 * `&.sb-words-of-jesus`), which a plain JSX text child would HTML-escape on
 * the server but not on the client, causing a hydration mismatch; Preact also
 * never diffs `dangerouslySetInnerHTML` during hydration, so this stays
 * inert even if the two sides' CSS text does legitimately differ.
 *
 * The SSR suspend below is deliberately scoped to just this component rather
 * than gating `MainContent` as a whole: `_renderToString`'s array-of-children
 * traversal doesn't block later siblings on an earlier one suspending, so
 * gating only here keeps everything else's first-render timing (most notably
 * `BibleReader`'s own chapter-load suspend) exactly as it is without a
 * `?customization=` link in play.
 */
export function ExternalResourceDependencies({
  themeCssVariables,
  themeCssClasses,
  googleFontFamilies,
  initialCustomizationLoadPromise,
  initialCustomizationLoadSettled,
}: {
  themeCssVariables: ReadonlySignal<string>;
  themeCssClasses: ReadonlySignal<string>;
  googleFontFamilies: ReadonlySignal<string[]>;
  initialCustomizationLoadPromise: Promise<void>;
  initialCustomizationLoadSettled: ReadonlySignal<boolean>;
}) {
  if (import.meta.env.SSR && !initialCustomizationLoadSettled.value) {
    throw initialCustomizationLoadPromise;
  }

  return (
    <>
      <link
        href="https://fonts.googleapis.com/css2?family=Newsreader:ital,opsz,wght@0,6..72,200..800;1,6..72,200..800&family=Plus+Jakarta+Sans:ital,wght@0,200..800;1,200..800&family=Roboto&family=Open+Sans&family=Playfair+Display&family=Cormorant+Garamond&display=swap"
        rel="stylesheet"
      />
      <link
        rel="stylesheet"
        href="https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@24,400,0,0"
      />
      {googleFontFamilies.value.length > 0 && (
        // A customization variant can name any Google Font by typing its
        // exact name — this loads whatever isn't already covered by the
        // static presets above. See CustomizationsManager.buildCustomFontValue
        // for why the name is already restricted to safe characters.
        <link
          rel="stylesheet"
          href={`https://fonts.googleapis.com/css2?${googleFontFamilies.value
            .map((name) => `family=${name.replace(/ /g, "+")}`)
            .join("&")}&display=swap`}
        />
      )}
      <style
        dangerouslySetInnerHTML={{
          __html: `body {\n${themeCssVariables.value}\n}`,
        }}
      />
      <style dangerouslySetInnerHTML={{ __html: themeCssClasses.value }} />
    </>
  );
}

export function Main({
  config: appConfig = DEFAULT_APP_CONFIG,
  initialHref,
  initialState,
}: {
  /** Deployment config (base path + asset host) injected by the host server. */
  config?: AppConfig;
  /** Full initial URL — passed during SSR where `window` is absent. */
  initialHref?: string;

  initialState?: ReturnType<typeof createSeedBibleState>;
} = {}) {
  // Split into two components rather than conditionally skipping `useMemo`
  // below (`initialState ?? useMemo(...)`): every real caller always passes
  // `initialState`, but if one ever didn't across a re-render, that would
  // change which hooks this component instance calls, which Preact requires
  // to stay identical for the component's lifetime. Choosing which of two
  // components to render carries no such requirement — each has its own,
  // internally-fixed hook sequence.
  return initialState ? (
    <MainWithState appConfig={appConfig} initialState={initialState} />
  ) : (
    <MainCreatingState appConfig={appConfig} initialHref={initialHref} />
  );
}

function MainWithState({
  appConfig,
  initialState,
}: {
  appConfig: AppConfig;
  initialState: ReturnType<typeof createSeedBibleState>;
}) {
  return <MainBody appConfig={appConfig} state={initialState} />;
}

function MainCreatingState({
  appConfig,
  initialHref,
}: {
  appConfig: AppConfig;
  initialHref?: string;
}) {
  const state = useMemo(
    () => createSeedBibleState({ config: appConfig, initialHref }),
    []
  );
  return <MainBody appConfig={appConfig} state={state} />;
}

function MainBody({
  appConfig,
  state,
}: {
  appConfig: AppConfig;
  state: ReturnType<typeof createSeedBibleState>;
}) {
  // Dev-only escape hatch for poking at live managers from the browser
  // console (e.g. `window.__seedBible.login`) — never runs in production.
  if (import.meta.env.DEV && typeof window !== "undefined") {
    (window as unknown as { __seedBible?: typeof state }).__seedBible = state;
  }

  useEffect(() => {
    state.extensions.loadDefaultExtensions();
  }, []);

  // One-time correction: the viewport signals seed to match the server's
  // UA-based guess so the first hydrate pass can't mismatch, but that guess
  // rarely matches the device's real size. Apply the real dimensions once,
  // right after Preact's first commit — a normal diffed re-render, not a
  // hydration mismatch.
  useEffect(() => {
    state.app.applyViewport();
  }, []);

  // Deferred real read: `login.localConfig` seeds empty to match SSR, so the
  // first hydrate pass can't disagree with the server over font size, UI
  // size, toolbar customization, disablePanels, theme, etc. Apply the
  // device's real saved config once, right after mount —
  // `SettingsManager`'s own effect() already re-derives `settings` whenever
  // `login.localConfig` changes, so no change is needed there.
  useEffect(() => {
    state.login.hydrateLocalConfig();
  }, []);

  // Deferred real read, same reason as the two above: saved tabs and their slot
  // layout, the cached translation catalog, the selector view mode, and the
  // tutorial/onboarding flags all seed to what the server rendered so the first
  // hydrate pass can't disagree with it, then get corrected here. Unlike the
  // others this one is load-bearing for correctness rather than polish — a
  // returning visitor's extra tabs would mount `TabRow`s and panes the served
  // HTML never had, which is the one divergence `hydrate()` reports instead of
  // silently patching.
  useEffect(() => {
    state.app.hydrateFromStorage();
  }, []);

  // Deferred real read, same reason as the three above: `isOpen` seeds
  // `false` to match SSR (which always renders Today closed, for crawler
  // SEO — see `TodayManager`), so the first hydrate pass can't disagree with
  // it. Apply the URL's real open/closed state, and start the live URL sync,
  // once right after mount.
  useEffect(() => {
    state.today.hydrateAutoOpen();
  }, []);

  if (typeof document !== "undefined") {
    useSignalEffect(() => {
      document.title = state.app.title.value;
    });
  }

  return (
    <AppConfigProvider value={appConfig}>
      <I18nProvider i18n={state.i18n}>
        <MainContent state={state} />
      </I18nProvider>
    </AppConfigProvider>
  );
}

function MainContent(props: {
  state: ReturnType<typeof createSeedBibleState>;
}) {
  const { state } = props;
  const { isRtl } = useI18n();
  const { renderedAsWebKit } = useAppConfig();
  const webkitClass = isWebKit(renderedAsWebKit) ? "is-webkit" : "";
  const appDirection = isRtl ? "rtl" : "ltr";
  const { theme, selector } = state;
  const sidePane =
    state.app.effectivePanes.value.find((pane) => pane.placement === "side") ??
    null;
  const fullscreenPane =
    state.app.effectivePanes.value.find(
      (pane) => pane.placement === "fullscreen"
    ) ?? null;

  return (
    <>
      <div
        className={`sb-app-root ${webkitClass}`}
        dir={appDirection}
        onClick={(e) => {
          if (!e.defaultPrevented) {
            closeContextMenus();
          }
        }}
        style={{
          display: "flex",
          height: "100dvh",
          overflow: "hidden",
        }}
      >
        <ExternalResourceDependencies
          themeCssVariables={theme.themeCssVariables}
          themeCssClasses={theme.themeCssClasses}
          googleFontFamilies={theme.googleFontFamiliesToLoad}
          initialCustomizationLoadPromise={
            state.customizations.initialCustomizationLoadPromise
          }
          initialCustomizationLoadSettled={
            state.customizations.initialCustomizationLoadSettled
          }
        />
        <Sidebar state={state} />

        <div className="sb-content-row">
          <main className="sb-main-content">
            <TabsLayout state={state} />
          </main>
          {sidePane && <SidePane state={state} pane={sidePane} />}
          {fullscreenPane && (
            <FullscreenPane state={state} pane={fullscreenPane} />
          )}
        </div>

        <PaneLayout state={state} />

        <ToastHost app={state.app} />

        {/* The selector draws its own tour spotlight/popover internally
              (CSS dim toggled off the tutorial signals), since its elements
              live in this portal's shadow root and can't be measured from
              the main tour overlay. */}
        <BibleSelector
          className={`${webkitClass}`}
          isOpen={selector.isOpen.value}
          onClose={() => selector.setOpen(false)}
          app={state.app}
          selectorState={selector}
          bibleDataManager={state.bibleData}
          tutorial={state.tutorial}
        />

        <FloatingReaderPanels state={state} />

        {/* The toolbar suspends during SSR until the reading position is
            known, so its chapter links land in the server-rendered HTML.
            Nothing suspends here on the client. */}
        <Suspense fallback={null}>
          <BibleReaderToolbar state={state} />
        </Suspense>

        <SharedSessionsToasts state={state} />

        <ModalHost manager={state.modals} />

        <LoginModal login={state.login} navigation={state.navigation} />

        <TermsOfServiceModal
          isOpen={state.isTermsOpen.value}
          onClose={() => state.closeTerms()}
        />

        <PrivacyPolicyModal
          isOpen={state.isPrivacyOpen.value}
          onClose={() => state.closePrivacy()}
        />

        <CodeOfConductModal
          isOpen={state.isCodeOfConductOpen.value}
          onClose={() => state.closeCodeOfConduct()}
        />

        <OnboardingModals
          onboarding={state.onboarding}
          os={state.os}
          toast={state.app.toast}
          className={`${webkitClass}`}
          customizationName={
            state.customizations.activeCustomization.value?.name
          }
        />

        <TutorialPrompt
          tutorial={state.tutorial}
          className={`${webkitClass}`}
          customizationName={
            state.customizations.activeCustomization.value?.name
          }
        />

        <OfflineDownloadPrompt
          offline={state.bibleData.offline}
          toast={state.app.toast}
          className={`${webkitClass}`}
        />

        <Tutorial
          tutorial={state.tutorial}
          className={`${webkitClass}`}
          groupFilter="non-selector"
        />

        <LanguageUnavailableModal className={`${webkitClass}`} />

        <UiLanguageSwitchModal className={`${webkitClass}`} />
      </div>
    </>
  );
}
